/**
 * 17 - Missile Convoy (10 §2): 2-4 mobile launchers parked side by side in a wired laager, crews in tents
 * beside them, the command car by the gate. Strict quiet until inside the wire: at alert 2 outside it the
 * engines start and 45 s later the launchers DRIVE OFF - they are removed from the map and the mission is lost.
 * Launchers are `hard` (LAW or bomb); a charge laid between two of them takes both.
 *
 * ROUND4 §1: they drive off for real. Each parked launcher has a `launcherTruck` behind it, woken by the same
 * consequence: the hardstanding empties, the trucks roll down the track to the gate and off the end of their
 * run. A launcher already wrecked sends nothing.
 */

import { siteEmplacement } from '../emplacements';
import { checker, clutter, guard, guardsAbout, insideRect, ruleOf, structure } from './helpers';
import { ON_ROAD, dress, put } from './helpers3a';
import { blastGap, checkRuleText, checkTargets, cooksOff } from './helpers3aAsserts';
import { roadRun, vehicle, vehiclesOf } from './helpers4Vehicles';
import type { ArchetypeSpec } from './types';

export const launchersFor = (threat: number): number => (threat <= 3 ? 2 : threat <= 6 ? 3 : 4);

/** Hull to hull 4.8 m: one launcher cooking off does not take the next, one bomb between two takes both. */
const SPACING = 8;
const DRIVE_OFF_SECONDS = 45;
/** The tag the drive-off wakes; the launchers pull out at this speed, in metres per second. */
const LEAVES = 'targetsLeave';
const CONVOY_SPEED = 6;
/** How far down the road out of the laager the run goes before they are away. */
const RUN_OUT = 30;

export const missileConvoy: ArchetypeSpec = {
  id: 'missile-convoy',
  name: 'MISSILE CONVOY',
  nativeBiome: 'desert',
  baseThreat: 6,
  lengthTiles: 160,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'wire',
  sealed: false,
  layout: 'compound',
  size: { w: [44, 52], d: [34, 34] },
  sizeFor: (threat) => ({ w: launchersFor(threat) * SPACING + 28, d: 34 }),
  river: false,
  patrolLoop: 'inside',
  breach: { min: 0, max: 0 },

  build(g) {
    const count = launchersFor(g.threat);
    const cx = (g.rect.x0 + g.rect.x1) / 2;
    // Nose to the south gate, north of the middle so the way out stays clear.
    const z = (g.rect.z0 + g.rect.z1) / 2 - 3;
    // The hardstanding they are parked on, and the track from it to the gate they would leave by.
    const gate = g.ctx.plan.roadEnd;
    const span = (count * SPACING) / 2;
    const off = Math.max(cx - span, Math.min(cx + span, gate.x));
    g.map.road([[cx - span, z], [cx + span, z]], 9);
    g.map.road([[off, z + 4], [off, z + 8.5], [gate.x, z + 8.5], [gate.x, g.rect.z1 - 6]], 4.2);
    const ids: number[] = [];
    const parked: Array<{ id: number; x: number; z: number }> = [];
    for (let i = 0; i < count; i++) {
      const x = cx + (i - (count - 1) / 2) * SPACING;
      const prop = put(g, 'mobileLauncher', x, z, { target: true, margin: 0.5, avoid: ON_ROAD });
      ids.push(prop.id);
      parked.push({ id: prop.id, x: prop.x, z: prop.z });
    }
    // R4: each launcher's own way out - off the hardstanding, along the track to the gate, then down the road.
    // Laid before the laager is dressed, so that nothing is ever built or scattered across it.
    const road = (g.map.roads[0]?.points ?? []).map(([rx, rz]) => ({ x: rx, z: rz }));
    const away = roadRun(road, g.rect.z1 - 4, Math.min(g.ctx.sizeZ - 6, g.rect.z1 + RUN_OUT));
    for (const launcher of parked) {
      vehicle(g, {
        kind: 'launcherTruck', speed: CONVOY_SPEED, wakeTag: LEAVES, replacesPropId: launcher.id,
        from: { x: launcher.x, z: launcher.z },
        route: [{ x: launcher.x, z: z + 5 }, { x: off, z: z + 8.5 }, { x: gate.x, z: g.rect.z1 - 6 }, ...away],
      });
    }

    // A nest covering the gate where there is a fair place for one: the laager does not depend on it.
    if (g.threat >= 4) siteEmplacement(g.ctx, g.rng, 'mgNest', { x: gate.x, z: g.rect.z1 }, g.elite);
    const west = cx - span - 7;
    const east = cx + span + 7;
    dress(g, 'staffCar', gate.x + (gate.x < cx ? 5 : -5), g.rect.z1 - 9, { avoid: ON_ROAD, margin: 0.5 });
    dress(g, g.kit.shelter, west, z - 4);
    dress(g, g.kit.shelter, east, z - 4);
    structure(g, 'alarmPost', { margin: 1 });
    clutter(g, 'barrel', 2, { x: west, z: z + 3 }, 3);
    clutter(g, 'crate', 3);
    guard(g, west + 3, z + 6, 0);
    guard(g, east - 3, z + 6, 0);
    guardsAbout(g, 1 + g.rng.int(2));

    const inside = { ...g.rect };
    return {
      goalZone: inside,
      objective: {
        kind: 'destroyTargets', label: 'DESTROY THE LAUNCHERS', targetPropIds: ids, minFraction: 1,
        rules: [
          { type: 'quietUntilZone', zone: inside, breach: 'penalty' },
          {
            type: 'alertConsequence', level: 2, delaySeconds: DRIVE_OFF_SECONDS, unlessInsideZone: inside,
            effect: 'targetsLeave',
            orders: 'NO ALARM UNTIL YOU ARE INSIDE THE WIRE, OR THE LAUNCHERS DRIVE OFF',
            warning: 'ENGINES RUNNING - THE LAUNCHERS LEAVE IN',
            reason: 'THE LAUNCHERS DROVE OFF',
          },
          { type: 'noContactBonus' },
          { type: 'recall', mode: 'normal' },
        ],
      },
    };
  },

  asserts(def) {
    const { check, out } = checker('missile-convoy');
    const launchers = checkTargets(check, def, ['mobileLauncher']);
    const wanted = launchersFor(def.threat);
    check(launchers.length === wanted, `needs ${wanted} launchers at threat ${def.threat}, has ${launchers.length}`);
    check(
      def.objective.kind === 'destroyTargets' && def.objective.minFraction === 1,
      'every launcher must be destroyed',
    );
    const quiet = ruleOf(def, 'quietUntilZone');
    check(quiet?.breach === 'penalty', 'needs the strict quiet rule until inside the laager');
    const leave = ruleOf(def, 'alertConsequence');
    check(
      leave?.effect === 'targetsLeave' && leave.level === 2 && leave.delaySeconds === DRIVE_OFF_SECONDS,
      `the launchers must drive off ${DRIVE_OFF_SECONDS} s after alert 2`,
    );
    check(leave?.unlessInsideZone !== undefined, 'the drive-off must be called off once the ranger is inside the wire');
    // R4: the launchers that leave are the launchers that were parked, one truck each, driving out of the laager.
    const trucks = vehiclesOf(def.spawns);
    check(trucks.length === wanted, `needs ${wanted} launcher trucks, has ${trucks.length}`);
    for (const truck of trucks) {
      check(truck.kind === 'launcherTruck', 'a launcher under way is a launcherTruck');
      check(truck.dormantUntil === 'timedEvent' && truck.wakeTag === LEAVES, 'the trucks wait on the drive-off');
      check(launchers.some((l) => l.id === truck.replacesPropId), 'each truck must be one of the parked launchers');
      const run = truck.route?.points.points ?? [];
      const exit = run[run.length - 1];
      check(truck.route?.loop === false && exit !== undefined && exit[1] > def.goalZone.z1,
        'a launcher must drive clear of the laager and off the end of its run');
    }
    check(ruleOf(def, 'noContactBonus') !== undefined, 'offers the no-contact bonus');
    checkRuleText(check, def);
    for (const l of launchers) {
      if (quiet) check(insideRect(quiet.zone, l.x, l.z), `launcher ${l.id} is parked outside the laager`);
      // Parked in one row, close enough for one charge between two hulls (a bomb spikes `hard` things within
      // 4.95 m), too far for one cooking off to take the next.
      check(launchers.every((o) => o.z === l.z), 'the launchers are parked in one row');
      check(launchers.every((o) => o.id === l.id || !cooksOff(l, o)), `launcher ${l.id} would cook off its neighbour`);
      check(
        launchers.some((o) => o.id !== l.id && blastGap(l, o) <= 8),
        `launcher ${l.id} is parked away from the others`,
      );
    }
    return out;
  },
};
